import { Box, Container, Typography, Grid, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const TalentFlow = () => {
  useDocumentTitle("Briozon - Talent Flow");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: "floatBall 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                 <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  TALENT FLOW
                </Typography>
                   <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Talent{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Flow
                  </Box>
                </Typography>
                 <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Streamline hiring with our intelligent ATS. Empower your team to recruit smarter, faster, and better 
                  with AI-powered automation and analytics.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: "bounce 2s infinite",
                  }}
                >
                  🔍
                </Box>

                {/* Floating stat cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "8%" },
                    left: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    5x
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Faster Hiring
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "5%" },
                    right: { xs: "5%", sm: "8%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 6s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    99%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Accuracy
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "8%", sm: "10%" },
                    left: { xs: "8%", sm: "15%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 7s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    100%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Secure Data
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "5%", sm: "8%" },
                    right: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5.5s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    24/7
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Support
                  </Typography>
                </Box>

                {/* Floating service icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "38%",
                    left: { xs: "0%", sm: "5%" },
                    width: 50,
                    height: 50,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    animation: `${floatBall} 4s ease-in-out infinite 0.8s`,
                    boxShadow: "0 4px 16px rgba(91, 124, 250, 0.2)",
                  }}
                >
                  🤝
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "35%",
                    right: { xs: "0%", sm: "3%" },
                    width: 50,
                    height: 50,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.15) 0%, rgba(91,124,250,0.15) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    animation: `${floatBall} 5s ease-in-out infinite 1.2s`,
                    boxShadow: "0 4px 16px rgba(118, 75, 162, 0.2)",
                  }}
                >
                  📊
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: { xs: "15%", sm: "20%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.2) 0%, rgba(118,75,162,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 6s ease-in-out infinite 0.3s`,
                  }}
                >
                  ⚡
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    right: { xs: "15%", sm: "20%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.2) 0%, rgba(91,124,250,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 6.5s ease-in-out infinite 0.7s`,
                  }}
                >
                  🔒
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
        {/* Add bounce keyframes for emoji animation */}
        <style>{`
          @keyframes bounce {
            0%, 100% { transform: scale(1); }
            25% { transform: scale(1.2) rotate(-5deg); }
            50% { transform: scale(1.1) rotate(5deg); }
            75% { transform: scale(1.15) rotate(-3deg); }
          }
        `}</style>
      </Box>
      {/* ── Section 2: Features Bento ── */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "#f8fafc",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* dot-grid */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.13) 1px, transparent 1px)", backgroundSize: "38px 38px", pointerEvents: "none" }} />
        {/* glow orbs */}
        <Box sx={{ position: "absolute", top: "-80px", right: "-60px", width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "-60px", left: "-60px", width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.10) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.8, borderRadius: "999px", background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)", border: "1px solid rgba(91,124,250,0.2)", mb: 2 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: 2 }}>🧑‍💼 PLATFORM FEATURES</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Everything You Need to{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Hire Smarter
              </Box>
            </Typography>
            <Typography sx={{ color: "#4c5472", fontSize: { xs: "16px", sm: "18px" }, lineHeight: 1.8, maxWidth: 620, mx: "auto" }}>
              TalentFlow brings AI, automation, and collaboration into one seamless ATS built for modern teams.
            </Typography>
          </Box>

          {/* Bento Row 1 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Hero Bento Card */}
            <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: "24px",
                  p: { xs: 3, sm: 4 },
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: { xs: 280, sm: 320 },
                  zIndex: 0,
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 24px 60px rgba(91,124,250,0.35)", zIndex: 2 },
                }}
              >
                {/* Ghost number */}
                <Typography sx={{ position: "absolute", top: -20, right: 24, fontSize: "160px", fontWeight: 900, color: "rgba(255,255,255,0.06)", lineHeight: 1, userSelect: "none", pointerEvents: "none" }}>01</Typography>
                {/* Badge */}
                <Box sx={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 0.8, px: 2, py: 0.7, borderRadius: "999px", background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)", mb: 3 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#a5f3fc", boxShadow: "0 0 6px #a5f3fc" }} />
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", letterSpacing: 1.5 }}>AI-POWERED ATS</Typography>
                </Box>
                {/* Emoji */}
                <Typography className="tf-emoji" sx={{ fontSize: { xs: "72px", sm: "96px" }, lineHeight: 1, mb: 2, display: "block", transition: "transform 0.5s cubic-bezier(0.34,1.56,0.64,1)", "&:hover": { transform: "scale(1.15) rotate(-6deg)" } }}>🔍</Typography>
                <Box>
                  <Typography sx={{ fontSize: { xs: "22px", sm: "28px", md: "32px" }, fontWeight: 800, color: "#fff", lineHeight: 1.25, mb: 1 }}>
                    Zero-Friction<br />Talent Acquisition
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: { xs: "14px", sm: "15px" }, lineHeight: 1.7 }}>
                    AI matches, scores, and ranks candidates so your team focuses on decisions — not paperwork.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Impact Metrics Card */}
            <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: "24px",
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  border: "1.5px solid rgba(91,124,250,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  zIndex: 0,
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 20px 48px rgba(91,124,250,0.12)", borderColor: "rgba(91,124,250,0.25)", zIndex: 2 },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#5b7cfa", letterSpacing: 2, textTransform: "uppercase" }}>Impact Metrics</Typography>
                {[
                  { val: "5×", label: "Faster Time-to-Hire", color: "#5b7cfa" },
                  { val: "99%", label: "Candidate Match Accuracy", color: "#764ba2" },
                  { val: "60%", label: "Reduction in Hiring Costs", color: "#6366f1" },
                  { val: "3×", label: "More Quality Applicants", color: "#8b5cf6" },
                ].map((m) => (
                  <Box key={m.label} sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: "14px", background: "linear-gradient(135deg, #f8faff 0%, #f3f0ff 100%)", border: "1px solid rgba(91,124,250,0.08)" }}>
                    <Typography sx={{ fontSize: { xs: "26px", sm: "30px" }, fontWeight: 900, color: m.color, minWidth: 60, lineHeight: 1 }}>{m.val}</Typography>
                    <Typography sx={{ fontSize: "13px", color: "#4c5472", fontWeight: 600, lineHeight: 1.4 }}>{m.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Bento Row 2 — 6 feature cards with left accent bar */}
          <Grid container spacing={3}>
            {[
              { icon: "🔍", title: "Smart Talent Search", desc: "AI-powered matching surfaces the best candidates instantly from millions of profiles.", color: "#5b7cfa" },
              { icon: "📊", title: "Analytics & Insights", desc: "Real-time dashboards track pipeline health, time-to-hire, and recruiter performance.", color: "#764ba2" },
              { icon: "🤝", title: "Team Collaboration", desc: "Structured interview kits, scorecards, and shared notes keep every stakeholder aligned.", color: "#6366f1" },
              { icon: "⚡", title: "Automated Workflows", desc: "Auto-advance candidates, send personalized emails, and trigger actions — no manual effort.", color: "#8b5cf6" },
              { icon: "💬", title: "Candidate Engagement", desc: "Personalized outreach, interview scheduling, and status updates keep talent warm.", color: "#a855f7" },
              { icon: "🔒", title: "Enterprise Security", desc: "SOC 2 compliant with role-based access, audit logs, and encrypted data at rest & transit.", color: "#7c3aed" },
            ].map((feat, i) => (
              <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: "18px",
                    p: { xs: 2.5, sm: 3 },
                    background: "#fff",
                    border: "1.5px solid rgba(91,124,250,0.10)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    position: "relative",
                    overflow: "hidden",
                    zIndex: 0,
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 16px 40px rgba(91,124,250,0.13)", borderColor: "rgba(91,124,250,0.22)", zIndex: 2 },
                    "&:hover .tf-feat-icon": { transform: "scale(1.25) rotate(-8deg)" },
                  }}
                >
                  {/* Left accent bar */}
                  <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, borderRadius: "18px 0 0 18px", background: feat.color }} />
                  {/* Icon */}
                  <Box className="tf-feat-icon" sx={{ fontSize: "32px", lineHeight: 1, transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1)", flexShrink: 0, mt: 0.3 }}>{feat.icon}</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: "16px", sm: "17px" }, color: "#1b1f33", mb: 0.5 }}>{feat.title}</Typography>
                    <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#4c5472", lineHeight: 1.65 }}>{feat.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Section 3: How It Works ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: "linear-gradient(135deg, #f3f0ff 0%, #e8f0fe 100%)", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: "30%", right: "-100px", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.8, borderRadius: "999px", background: "rgba(91,124,250,0.1)", border: "1px solid rgba(91,124,250,0.2)", mb: 2 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: 2 }}>🗺️ HOW IT WORKS</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 2 }}>
              From Job Post to{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Day One
              </Box>
            </Typography>
            <Typography sx={{ color: "#4c5472", fontSize: { xs: "15px", sm: "17px" }, lineHeight: 1.8, maxWidth: 560, mx: "auto" }}>
              A streamlined four-step process that eliminates bottlenecks and delivers hires faster.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              { step: "01", icon: "📋", title: "Define the Role", desc: "Build smart job descriptions with AI suggestions, set skills requirements, and publish across all major job boards in one click.", color: "#5b7cfa" },
              { step: "02", icon: "🤖", title: "AI Sourcing & Screening", desc: "Our engine scans thousands of profiles, ranks applicants by fit score, and auto-rejects clearly unqualified candidates — saving hours.", color: "#764ba2" },
              { step: "03", icon: "📊", title: "Evaluate & Collaborate", desc: "Structured interview scorecards, async video responses, and team feedback threads converge into a clear hiring decision.", color: "#6366f1" },
              { step: "04", icon: "🎉", title: "Offer & Onboard", desc: "Send digital offer letters, collect e-signatures, and trigger onboarding workflows — all without leaving TalentFlow.", color: "#8b5cf6" },
            ].map((step) => (
              <Grid item xs={12} sm={6} md={3} key={step.step} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: "22px",
                    p: { xs: 3, sm: 3.5 },
                    background: "#fff",
                    border: "1.5px solid rgba(91,124,250,0.10)",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                    zIndex: 0,
                    transition: "all 0.35s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: `0 20px 48px ${step.color}28`, borderColor: step.color + "55", zIndex: 2 },
                  }}
                >
                  {/* Ghost step number */}
                  <Typography sx={{ position: "absolute", top: -10, right: 16, fontSize: "88px", fontWeight: 900, color: step.color, opacity: 0.07, lineHeight: 1, userSelect: "none", pointerEvents: "none" }}>{step.step}</Typography>
                  {/* Step badge */}
                  <Box sx={{ display: "inline-flex", alignSelf: "flex-start", px: 1.8, py: 0.5, borderRadius: "999px", background: step.color + "18", mb: 2.5 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: step.color, letterSpacing: 1.5 }}>STEP {step.step}</Typography>
                  </Box>
                  {/* Icon */}
                  <Typography sx={{ fontSize: "52px", mb: 2, lineHeight: 1 }}>{step.icon}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: "17px", sm: "19px" }, color: "#1b1f33", mb: 1 }}>{step.title}</Typography>
                  <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#4c5472", lineHeight: 1.7 }}>{step.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Section 4: Built For Every Team ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: "#f8fafc", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.10) 1px, transparent 1px)", backgroundSize: "38px 38px", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.8, borderRadius: "999px", background: "rgba(91,124,250,0.10)", border: "1px solid rgba(91,124,250,0.18)", mb: 2 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#5b7cfa", letterSpacing: 2 }}>👥 WHO IT'S FOR</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 2 }}>
              Built for Every{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Hiring Team
              </Box>
            </Typography>
            <Typography sx={{ color: "#4c5472", fontSize: { xs: "15px", sm: "17px" }, lineHeight: 1.8, maxWidth: 560, mx: "auto" }}>
              Whether you're scaling fast or hiring one critical role, TalentFlow adapts to your workflow.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              { icon: "🏢", title: "Enterprise HR", desc: "Multi-department pipelines, advanced RBAC, and compliance tools built for scale." },
              { icon: "🚀", title: "High-Growth Startups", desc: "Move fast with lightweight templates, quick setup, and no bloated feature tax." },
              { icon: "🏥", title: "Healthcare Hiring", desc: "Credential verification, licensure tracking, and compliance-ready audit trails." },
              { icon: "💻", title: "Tech Recruiting", desc: "Integrated coding assessments, GitHub parsing, and skill-based ranking for engineering roles." },
              { icon: "🎓", title: "Campus Recruitment", desc: "Bulk event management, university partnerships, and cohort-based onboarding flows." },
              { icon: "🌍", title: "Remote-First Teams", desc: "Async video interviews, global timezone scheduling, and distributed team scorecards." },
            ].map((team, i) => (
              <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: "20px",
                    p: { xs: 2.5, sm: 3 },
                    background: "#fff",
                    border: "1.5px solid rgba(91,124,250,0.10)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    zIndex: 0,
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 16px 40px rgba(91,124,250,0.14)", borderColor: "rgba(91,124,250,0.28)", zIndex: 2 },
                    "&:hover .tf-team-icon": { transform: "scale(1.2) rotate(-8deg)" },
                  }}
                >
                  <Typography className="tf-team-icon" sx={{ fontSize: "40px", lineHeight: 1, flexShrink: 0, transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>{team.icon}</Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: "15px", sm: "16px" }, color: "#1b1f33", mb: 0.5 }}>{team.title}</Typography>
                    <Typography sx={{ fontSize: { xs: "13px", sm: "13.5px" }, color: "#4c5472", lineHeight: 1.6 }}>{team.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* Contact Section before footer */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Optimize Your Recruitment Process
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              We’re here to help you optimize your recruitment process with our advanced ATS. Reach out for any inquiries or support, and we’ll guide you through every step. Contact us below and let’s get started!
            </Typography>
            <Button
              component={"a"}
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TalentFlow;
